<template>
	<div>
		<!-- Граница выделенной группы -->
		<div v-if="showGroupBorder" class="group-boundary" :style="groupBoundaryStyle"></div>

		<!-- Кнопка группировки (появляется по правому клику) -->
		<div v-if="showContextMenu" class="group-context-menu" :style="contextMenuStyle" @click.stop>
			<button @click="emitGroupCreation">Группировать</button>
		</div>

		<!-- Прямоугольник выделения -->
		<div v-if="isSelecting" class="selection-box" :style="selectionBoxStyle"></div>
	</div>
</template>

<script>
export default {
	name: 'GroupingComponent',
	props: {
		selectedItems: {
			type: Array,
			required: false,
			default: () => []
		},
		contentRef: {
			type: HTMLElement,
			required: true
		}
	},
	data() {
		return {
			showGroupBorder: false,
			showContextMenu: false,
			contextMenuPosition: { x: 0, y: 0 },
			selectionStart: null,
			selectionBox: {
				x: 0,
				y: 0,
				width: 0,
				height: 0
			},
			isSelecting: false
		}
	},
	watch: {
		selectedItems: {
			handler(newVal) {
				this.showGroupBorder = newVal?.length > 1
			},
			immediate: true
		}
	},
	computed: {
		groupBoundaryStyle() {
			if (!this.selectedItems || this.selectedItems.length < 2) return {}

			// Вычисляем общие границы для всех выделенных элементов
			let minX = Infinity
			let minY = Infinity
			let maxX = -Infinity
			let maxY = -Infinity

			this.selectedItems.forEach(item => {
				if (!item) return
				const rect = this.getItemRect(item)
				minX = Math.min(minX, rect.left)
				minY = Math.min(minY, rect.top)
				maxX = Math.max(maxX, rect.left + rect.width)
				maxY = Math.max(maxY, rect.top + rect.height)
			})

			return {
				left: `${minX}px`,
				top: `${minY}px`,
				width: `${maxX - minX}px`,
				height: `${maxY - minY}px`,
				position: 'absolute',
				border: '2px dashed #4285f4',
				pointerEvents: 'none',
				zIndex: 9998
			}
		},

		contextMenuStyle() {
			return {
				left: `${this.contextMenuPosition.x}px`,
				top: `${this.contextMenuPosition.y}px`,
				position: 'absolute',
				zIndex: 9999
			}
		},

		selectionBoxStyle() {
			return {
				left: `${this.selectionBox.x}px`,
				top: `${this.selectionBox.y}px`,
				width: `${this.selectionBox.width}px`,
				height: `${this.selectionBox.height}px`,
				position: 'absolute',
				backgroundColor: 'rgba(66, 133, 244, 0.2)',
				border: '1px solid #4285f4',
				pointerEvents: 'none',
				zIndex: 9997
			}
		}
	},
	methods: {
		getItemRect(item) {
			if (!item || !this.contentRef) return { left: 0, top: 0, width: 0, height: 0 }

			// Получаем DOM-элемент по ID
			const element = this.contentRef.querySelector(`[data-item-id="${item.id}"]`) ||
				this.contentRef.querySelector(`[data-image-id="${item.id}"]`)
			if (!element) return { left: 0, top: 0, width: 0, height: 0 }

			const rect = element.getBoundingClientRect()
			const containerRect = this.contentRef.getBoundingClientRect()

			return {
				left: rect.left - containerRect.left,
				top: rect.top - containerRect.top,
				width: rect.width,
				height: rect.height
			}
		},

		startSelection(e) {
			if (e.button !== 0) return // Только левая кнопка мыши

			this.isSelecting = true
			const containerRect = this.contentRef.getBoundingClientRect()
			this.selectionStart = {
				x: e.clientX - containerRect.left,
				y: e.clientY - containerRect.top
			}

			this.selectionBox = {
				x: this.selectionStart.x,
				y: this.selectionStart.y,
				width: 0,
				height: 0
			}

			this.showGroupBorder = false
			this.showContextMenu = false
			this.$emit('clear-selection')
		},

		handleSelection(e) {
			if (!this.isSelecting || !this.selectionStart) return

			const containerRect = this.contentRef.getBoundingClientRect()
			const currentX = e.clientX - containerRect.left
			const currentY = e.clientY - containerRect.top

			this.selectionBox = {
				x: Math.min(this.selectionStart.x, currentX),
				y: Math.min(this.selectionStart.y, currentY),
				width: Math.abs(currentX - this.selectionStart.x),
				height: Math.abs(currentY - this.selectionStart.y)
			}
		},

		endSelection() {
			if (!this.isSelecting) return
			this.isSelecting = false

			// Находим элементы, попавшие в зону выделения
			const selectionRect = this.selectionBox
			const allElements = Array.from(this.contentRef.querySelectorAll('[data-item-id], [data-image-id]'))

			const selected = allElements.filter(el => {
				const elRect = el.getBoundingClientRect()
				const containerRect = this.contentRef.getBoundingClientRect()
				const elLeft = elRect.left - containerRect.left
				const elTop = elRect.top - containerRect.top

				return (
					elLeft + elRect.width > selectionRect.x &&
					elLeft < selectionRect.x + selectionRect.width &&
					elTop + elRect.height > selectionRect.y &&
					elTop < selectionRect.y + selectionRect.height
				)
			}).map(el => {
				return {
					id: el.dataset.itemId || el.dataset.imageId
				}
			})

			this.$emit('update-selection', selected)
		},

		handleContextMenu(e) {
			if (!this.selectedItems || this.selectedItems.length < 2) return

			e.preventDefault()

			const containerRect = this.contentRef.getBoundingClientRect()
			this.contextMenuPosition = {
				x: e.clientX - containerRect.left,
				y: e.clientY - containerRect.top
			}

			this.showContextMenu = true
		},

		emitGroupCreation() {
			this.showContextMenu = false
			this.$emit('create-group', this.selectedItems)
		},

		closeContextMenu() {
			this.showContextMenu = false
		}
	},
	mounted() {
		document.addEventListener('click', this.closeContextMenu)
	},
	beforeUnmount() {
		document.removeEventListener('click', this.closeContextMenu)
	}
}
</script>

<style scoped>
.group-boundary {
	pointer-events: none;
}

.group-context-menu {
	background: white;
	border: 1px solid #ccc;
	border-radius: 4px;
	padding: 8px;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.group-context-menu button {
	background: #4285f4;
	color: white;
	border: none;
	padding: 6px 12px;
	border-radius: 4px;
	cursor: pointer;
}

.group-context-menu button:hover {
	background: #3367d6;
}

.selection-box {
	background: rgba(66, 133, 244, 0.2);
	border: 1px solid #4285f4;
	pointer-events: none;
}
</style>